import { useState, useEffect } from "react";
import {
  Loader2,
  Lock,
  Globe,
  Github,
  Search,
  ChevronLeft,
  CheckCircle2,
} from "lucide-react";
import {
  api,
  type GitHubInstallation,
  type GitHubRepo,
} from "@/lib/api";

type Step = "name" | "repo" | "creating" | "done";
type RepoMode = "pick" | "create";

interface NewProjectFormProps {
  installations: GitHubInstallation[];
  onCreated: (projectId: string) => void;
  onCancel: () => void;
}

interface InstallationRepos {
  installation: GitHubInstallation;
  repos: GitHubRepo[];
  loading: boolean;
}

export function NewProjectForm({
  installations,
  onCreated,
  onCancel,
}: NewProjectFormProps) {
  const [step, setStep] = useState<Step>("name");
  const [projectName, setProjectName] = useState("");
  const [repoMode, setRepoMode] = useState<RepoMode>("pick");

  // Pick existing repo state
  const [groups, setGroups] = useState<InstallationRepos[]>([]);
  const [filter, setFilter] = useState("");

  // Create new repo state
  const [newRepoName, setNewRepoName] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [ownerId, setOwnerId] = useState(installations[0]?.id ?? "");

  const [error, setError] = useState<string | null>(null);
  const [statusText, setStatusText] = useState("");

  // Keep ownerId in sync if installations load after mount
  useEffect(() => {
    if (!ownerId && installations.length > 0) {
      setOwnerId(installations[0].id);
    }
  }, [installations, ownerId]);

  // Load repos when stepping to repo selection
  useEffect(() => {
    if (step !== "repo" || repoMode !== "pick") return;

    const initial = installations.map((inst) => ({
      installation: inst,
      repos: [] as GitHubRepo[],
      loading: true,
    }));
    setGroups(initial);

    installations.forEach((inst, idx) => {
      api.github
        .repos(inst.id)
        .then((repos) => {
          setGroups((prev) =>
            prev.map((g, i) =>
              i === idx ? { ...g, repos, loading: false } : g,
            ),
          );
        })
        .catch(() => {
          setGroups((prev) =>
            prev.map((g, i) =>
              i === idx ? { ...g, loading: false } : g,
            ),
          );
        });
    });
  }, [step, repoMode, installations]);

  function handleNameNext() {
    if (!projectName.trim()) return;
    setStep("repo");
  }

  async function handlePickRepo(
    installationId: string,
    repo: GitHubRepo,
  ) {
    setStep("creating");
    setStatusText("Creating project...");
    setError(null);
    try {
      const project = await api.projects.create(projectName.trim());

      setStatusText("Connecting repo...");
      await api.projects.connectRepo(project.id, {
        installation_id: installationId,
        github_repo_id: repo.id,
        repo_full_name: repo.full_name,
        default_branch: repo.default_branch,
      });

      setStatusText("Setting up agent...");
      // Small delay to let provisioning start
      await new Promise((r) => setTimeout(r, 1500));

      setStep("done");
      setStatusText("Opening project...");
      // Brief pause so user sees success before navigating
      await new Promise((r) => setTimeout(r, 500));
      onCreated(project.id);
    } catch (err) {
      console.error("[NewProjectForm] handlePickRepo failed:", err);
      const msg = err instanceof Error ? err.message : "Failed to create project";
      setError(msg);
      setStep("repo");
    }
  }

  async function handleCreateRepo() {
    const effectiveOwner = ownerId || installations[0]?.id;
    if (!newRepoName.trim() || !effectiveOwner) {
      setError("Missing repo name or GitHub account");
      return;
    }

    setStep("creating");
    setStatusText("Creating repository...");
    setError(null);
    try {
      const repo = await api.github.createRepo({
        installation_id: effectiveOwner,
        name: newRepoName.trim(),
        private: isPrivate,
      });

      setStatusText("Creating project...");
      const project = await api.projects.create(projectName.trim());

      setStatusText("Connecting repo...");
      await api.projects.connectRepo(project.id, {
        installation_id: effectiveOwner,
        github_repo_id: repo.id,
        repo_full_name: repo.full_name,
        default_branch: repo.default_branch,
      });

      setStatusText("Setting up agent...");
      await new Promise((r) => setTimeout(r, 1500));

      setStep("done");
      setStatusText("Opening project...");
      await new Promise((r) => setTimeout(r, 500));
      onCreated(project.id);
    } catch (err) {
      console.error("[NewProjectForm] handleCreateRepo failed:", err);
      const msg = err instanceof Error ? err.message : "Failed to create project";
      setError(msg);
      setStep("repo");
    }
  }

  const lowerFilter = filter.toLowerCase();

  // ── Creating / done state ──
  if (step === "creating" || step === "done") {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        {step === "done" ? (
          <CheckCircle2 size={20} className="text-green-400" />
        ) : (
          <Loader2 size={20} className="animate-spin text-daydra-accent" />
        )}
        <span className="text-xs text-daydra-dim">{statusText}</span>
      </div>
    );
  }

  // ── Step 1: Project name ──
  if (step === "name") {
    return (
      <div className="space-y-3">
        <input
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleNameNext();
          }}
          placeholder="Project name"
          className="w-full text-xs bg-daydra-raised rounded-lg px-3 py-2.5 text-daydra-text placeholder:text-daydra-muted border border-white/[0.06] outline-none focus:border-daydra-accent/30"
          autoFocus
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleNameNext}
            disabled={!projectName.trim()}
            className="text-xs text-daydra-accent hover:text-daydra-accent/80 disabled:opacity-50 transition-colors py-1"
          >
            Next
          </button>
          <button
            onClick={onCancel}
            className="text-xs text-daydra-muted hover:text-daydra-dim transition-colors py-1"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: Repo selection ──
  return (
    <div className="space-y-2">
      {/* Error banner */}
      {error && (
        <div className="rounded-lg bg-daydra-error/10 border border-daydra-error/20 px-3 py-2">
          <p className="text-xs text-daydra-error">{error}</p>
        </div>
      )}

      {/* Header with back button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { setStep("name"); setError(null); }}
          className="text-daydra-muted hover:text-daydra-dim transition-colors shrink-0 p-1"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="text-xs text-daydra-dim truncate flex-1">
          {projectName}
        </span>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 p-0.5 rounded-lg bg-white/[0.03]">
        <button
          onClick={() => { setRepoMode("pick"); setError(null); }}
          className={`flex-1 text-2xs py-1.5 rounded-md transition-colors ${
            repoMode === "pick"
              ? "bg-white/[0.06] text-daydra-text"
              : "text-daydra-muted hover:text-daydra-dim"
          }`}
        >
          Existing repo
        </button>
        <button
          onClick={() => { setRepoMode("create"); setError(null); }}
          className={`flex-1 text-2xs py-1.5 rounded-md transition-colors ${
            repoMode === "create"
              ? "bg-white/[0.06] text-daydra-text"
              : "text-daydra-muted hover:text-daydra-dim"
          }`}
        >
          New repo
        </button>
      </div>

      {/* ── Pick existing repo ── */}
      {repoMode === "pick" && (
        <>
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-daydra-raised border border-white/[0.06]">
            <Search size={12} className="text-daydra-muted shrink-0" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search repos..."
              className="flex-1 text-xs bg-transparent text-daydra-text placeholder:text-daydra-muted outline-none"
              autoFocus
            />
          </div>

          <div className="max-h-48 overflow-y-auto rounded-lg border border-white/[0.06]">
            {groups.map((group) => {
              const filtered = group.repos.filter((r) =>
                r.full_name.toLowerCase().includes(lowerFilter),
              );
              if (!group.loading && filtered.length === 0 && filter)
                return null;

              return (
                <div key={group.installation.id}>
                  <div className="sticky top-0 px-3 py-1.5 text-2xs text-daydra-muted bg-daydra-raised/80 backdrop-blur-sm border-b border-white/[0.04]">
                    {group.installation.account_login}
                  </div>
                  {group.loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2
                        size={14}
                        className="animate-spin text-daydra-muted"
                      />
                    </div>
                  ) : (
                    filtered.map((repo) => (
                      <button
                        key={repo.id}
                        onClick={() =>
                          handlePickRepo(group.installation.id, repo)
                        }
                        className="flex items-center gap-2 w-full px-3 py-2.5 hover:bg-white/[0.04] active:bg-white/[0.08] transition-colors"
                      >
                        <Github
                          size={13}
                          className="text-daydra-muted shrink-0"
                        />
                        <span className="text-xs text-daydra-dim truncate flex-1 text-left">
                          {repo.full_name}
                        </span>
                        {repo.private ? (
                          <Lock
                            size={10}
                            className="text-daydra-muted shrink-0"
                          />
                        ) : (
                          <Globe
                            size={10}
                            className="text-daydra-muted shrink-0"
                          />
                        )}
                      </button>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Create new repo ── */}
      {repoMode === "create" && (
        <div className="space-y-3">
          <input
            type="text"
            value={newRepoName}
            onChange={(e) => setNewRepoName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateRepo();
            }}
            placeholder="repository-name"
            className="w-full text-xs bg-daydra-raised rounded-lg px-3 py-2.5 text-daydra-text placeholder:text-daydra-muted border border-white/[0.06] outline-none focus:border-daydra-accent/30"
            autoFocus
          />

          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsPrivate(true)}
              className={`flex items-center gap-1.5 text-xs py-1 transition-colors ${
                isPrivate
                  ? "text-daydra-text"
                  : "text-daydra-muted hover:text-daydra-dim"
              }`}
            >
              <Lock size={11} />
              Private
            </button>
            <button
              onClick={() => setIsPrivate(false)}
              className={`flex items-center gap-1.5 text-xs py-1 transition-colors ${
                !isPrivate
                  ? "text-daydra-text"
                  : "text-daydra-muted hover:text-daydra-dim"
              }`}
            >
              <Globe size={11} />
              Public
            </button>
          </div>

          {installations.length > 1 && (
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="w-full text-xs bg-daydra-raised rounded-lg px-3 py-2.5 text-daydra-dim border border-white/[0.06] outline-none"
            >
              {installations.map((inst) => (
                <option key={inst.id} value={inst.id}>
                  {inst.account_login}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={handleCreateRepo}
            disabled={!newRepoName.trim()}
            className="w-full text-xs text-daydra-accent hover:text-daydra-accent/80 active:text-daydra-accent/60 disabled:opacity-50 transition-colors py-2 rounded-lg bg-daydra-accent/10 hover:bg-daydra-accent/15"
          >
            Create
          </button>
        </div>
      )}

      <button
        onClick={onCancel}
        className="text-xs text-daydra-muted hover:text-daydra-dim transition-colors py-1"
      >
        Cancel
      </button>
    </div>
  );
}
